import React from "react";
import { Link } from "react-router-dom";
import { CompassMark } from "@/components/CompassMark";

/**
 * NextRoom, a page-bottom pathway module.
 *
 * Every major page ends with this. It offers 2 or 3 curated pathways
 * with editorial "if you want X, go here" phrasing so the visitor always
 * has an obvious and interesting next place to go.
 *
 * Props:
 *   eyebrow?: string  (defaults to "Next")
 *   title?: string    (defaults to "Where to next.")
 *   rooms: [{ label, to, hint }]   // 2 or 3
 */
export const NextRoom = ({
  eyebrow = "Next",
  title = "Where to next.",
  rooms = [],
  className = "",
  testId = "next-room",
}) => {
  if (!rooms || rooms.length === 0) return null;
  return (
    <section
      data-testid={testId}
      aria-label="Curated next pathways"
      className={`relative bg-ink-base border-t border-ink-line ${className}`}
    >
      <div className="container-edge py-20 md:py-28 relative">
        <div className="flex items-end justify-between gap-6 mb-12 md:mb-16">
          <div>
            <p className="eyebrow-gold mb-4">{eyebrow}</p>
            <h2 className="display-serif text-3xl md:text-5xl text-paper-primary text-balance leading-[1.05]">
              {title}
            </h2>
          </div>
          <CompassMark size={40} opacity={0.35} className="hidden md:block" alt="" />
        </div>

        <ul className={`grid grid-cols-1 ${rooms.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2"} gap-px bg-ink-line`}>
          {rooms.map((room, i) => (
            <li key={`${room.to}-${i}`} className="bg-ink-base">
              <Link
                to={room.to}
                data-testid={`${testId}-link-${i}`}
                className="group block p-8 md:p-10 h-full flex flex-col justify-between min-h-[220px] hover:bg-ink-surface/50 transition-colors duration-500"
              >
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  {room.hint && (
                    <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-paper-tertiary">
                      If you want the {room.hint}
                    </p>
                  )}
                  <p className="mt-2 font-serif text-2xl md:text-3xl text-paper-primary group-hover:text-gold-warm transition-colors duration-300 text-balance leading-tight">
                    {room.label}
                  </p>
                </div>
                <span className="mt-8 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-paper-tertiary group-hover:text-paper-primary">
                  Enter <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default NextRoom;
